import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useTopCourses, useAnalyticsCurrency, type TopCourseTierBreakdown } from '@/hooks/useDashboardAnalytics';
import { Skeleton } from '@/components/ui/skeleton';
import { BookOpen, CreditCard, Key, ShoppingCart, TrendingUp, Package } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import type { PeriodValue } from './PeriodSelector';

interface TopCoursesTableProps {
  period: PeriodValue;
  limit?: number;
  className?: string;
}

const TYPE_CONFIG: Record<string, { label: string; icon: typeof BookOpen; variant: 'default' | 'secondary' | 'outline' }> = {
  COURSE: { label: 'Курс', icon: BookOpen, variant: 'default' },
  SUBSCRIPTION_PLAN: { label: 'Абонемент', icon: CreditCard, variant: 'secondary' },
  CHANNEL_PASS: { label: 'Пропуск', icon: Key, variant: 'outline' },
  EVENT: { label: 'Событие', icon: Package, variant: 'outline' },
};

export function TopCoursesTable({ period, limit = 10, className }: TopCoursesTableProps) {
  const { data, isLoading } = useTopCourses(
    limit,
    period.type,
    period.startDate,
    period.endDate
  );
  const { data: currencyData } = useAnalyticsCurrency();
  const baseCurrency = currencyData?.currency ?? 'USD';

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('ru-RU', {
      style: 'currency',
      currency: baseCurrency,
      maximumFractionDigits: 0,
    }).format(value);
  };

  if (isLoading) {
    return (
      <Card className={className}>
        <CardHeader>
          <CardTitle className="text-sm font-medium">Топ продуктов</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="flex items-center gap-3">
                <Skeleton className="h-10 w-10 rounded" />
                <div className="flex-1 space-y-1">
                  <Skeleton className="h-4 w-[200px]" />
                  <Skeleton className="h-3 w-[100px]" />
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  const products = data?.courses ?? [];

  if (products.length === 0) {
    return (
      <Card className={className}>
        <CardHeader>
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <ShoppingCart className="h-4 w-4" />
            Топ продуктов
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center h-[200px] text-muted-foreground">
            Нет данных о продажах
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle className="text-sm font-medium flex items-center gap-2">
          <ShoppingCart className="h-4 w-4" />
          Топ продуктов
        </CardTitle>
      </CardHeader>
      <CardContent>
        <TooltipProvider delayDuration={150}>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[40px]">#</TableHead>
                <TableHead>Продукт</TableHead>
                <TableHead>Тарифы</TableHead>
                <TableHead className="text-right">
                  <div className="flex items-center justify-end gap-1">
                    <ShoppingCart className="h-3 w-3" />
                    Продажи
                  </div>
                </TableHead>
                <TableHead className="text-right">
                  <div className="flex items-center justify-end gap-1">
                    <TrendingUp className="h-3 w-3" />
                    Выручка
                  </div>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {products.map((product, index) => {
                const typeConfig = TYPE_CONFIG[product.targetType ?? 'COURSE'] ?? TYPE_CONFIG.COURSE;
                const TypeIcon = typeConfig.icon;
                return (
                  <TableRow key={`${product.targetType}-${product.id}`}>
                    <TableCell className="font-medium text-muted-foreground">
                      {index + 1}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <Avatar className="h-10 w-10 rounded">
                          <AvatarImage src={product.thumbnailUrl ?? undefined} />
                          <AvatarFallback className="rounded">
                            <TypeIcon className="h-4 w-4" />
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex flex-col gap-1 min-w-0">
                          <span className="font-medium truncate max-w-[160px]">
                            {product.title}
                          </span>
                          <Badge variant={typeConfig.variant} className="w-fit text-[10px] px-1.5 py-0">
                            {typeConfig.label}
                          </Badge>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <TierBadges tiers={product.tiers ?? []} formatCurrency={formatCurrency} />
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {product.purchases.toLocaleString('ru-RU')}
                    </TableCell>
                    <TableCell className="text-right tabular-nums font-medium">
                      {formatCurrency(product.revenue)}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TooltipProvider>
      </CardContent>
    </Card>
  );
}

interface TierBadgesProps {
  tiers: readonly TopCourseTierBreakdown[];
  formatCurrency: (value: number) => string;
}

function TierBadges({ tiers, formatCurrency }: TierBadgesProps) {
  if (tiers.length === 0) {
    return <span className="text-xs text-muted-foreground">—</span>;
  }

  const visible = tiers.slice(0, 2);
  const hidden = tiers.slice(2);

  return (
    <div className="flex flex-wrap gap-1 max-w-[180px]">
      {visible.map((tier) => (
        <Tooltip key={`${tier.tierId ?? 'none'}-${tier.durationDays ?? 'x'}`}>
          <TooltipTrigger asChild>
            <Badge variant="outline" className="text-[10px] px-1.5 py-0 tabular-nums cursor-default">
              {tier.tierLabel} · {tier.purchases}
            </Badge>
          </TooltipTrigger>
          <TooltipContent>
            <div className="text-xs space-y-0.5">
              <div className="font-medium">{tier.tierLabel}</div>
              <div>Продаж: {tier.purchases}</div>
              <div>Выручка: {formatCurrency(tier.revenue)}</div>
            </div>
          </TooltipContent>
        </Tooltip>
      ))}
      {hidden.length > 0 && (
        <Tooltip>
          <TooltipTrigger asChild>
            <Badge variant="secondary" className="text-[10px] px-1.5 py-0 cursor-default">
              +{hidden.length}
            </Badge>
          </TooltipTrigger>
          <TooltipContent>
            <div className="text-xs space-y-1">
              {hidden.map((tier) => (
                <div key={`${tier.tierId ?? 'none'}-${tier.durationDays ?? 'x'}`}>
                  {tier.tierLabel} · {tier.purchases} · {formatCurrency(tier.revenue)}
                </div>
              ))}
            </div>
          </TooltipContent>
        </Tooltip>
      )}
    </div>
  );
}
